import { useCallback, useEffect, useRef, useState } from 'react';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'http://127.0.0.1:54321';
const EDGE_FN_URL = `${supabaseUrl}/functions/v1/admin-family-context`;

type Status = 'idle' | 'loading' | 'success' | 'error';

/** 'canary' = only the households switched on here; 'all' = everyone. */
export type FamilyContextMode = 'canary' | 'all';

export interface FamilyContextHousehold {
  household_id: string;
  name: string | null;
  primary_email: string | null;
  primary_name: string | null;
  member_count: number;
  enabled: boolean;
  enabled_at: string | null;
  enabled_by: string | null;
  created_at: string;
}

export interface FamilyContextResponse {
  /** EXTRACTION_CONTEXT_LEVEL — the ceiling; 0 = off for everyone. */
  context_level: number;
  mode: FamilyContextMode;
  households: FamilyContextHousehold[];
}

export type FamilyContextChange =
  | { household_id: string; enabled: boolean }
  | { mode: FamilyContextMode };

interface UseAdminFamilyContextResult {
  status: Status;
  data: FamilyContextResponse | null;
  error: string | null;
  /** Whether a PATCH is in flight. */
  saving: boolean;
  saveError: string | null;
  save: (change: FamilyContextChange) => Promise<boolean>;
  refetch: () => void;
}

async function readError(res: Response): Promise<string> {
  const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  return body.error ?? `HTTP ${res.status}`;
}

/**
 * The Family Context switch from the password-gated admin-family-context
 * edge function: one GET on mount, and a PATCH per change that answers with
 * the fresh state, so the table never shows a switch the server did not keep.
 */
export function useAdminFamilyContext(
  password: string,
): UseAdminFamilyContextResult {
  const [status, setStatus] = useState<Status>('idle');
  const [data, setData] = useState<FamilyContextResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!password || inFlight.current) return;
    inFlight.current = true;
    setStatus('loading');
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'GET',
        headers: { 'X-Admin-Password': password },
      });
      if (!res.ok) throw new Error(await readError(res));
      setData((await res.json()) as FamilyContextResponse);
      setStatus('success');
      setError(null);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      inFlight.current = false;
    }
  }, [password]);

  const save = useCallback(
    async (change: FamilyContextChange) => {
      setSaving(true);
      setSaveError(null);
      try {
        const res = await fetch(EDGE_FN_URL, {
          method: 'PATCH',
          headers: {
            'X-Admin-Password': password,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(change),
        });
        if (!res.ok) throw new Error(await readError(res));
        setData((await res.json()) as FamilyContextResponse);
        return true;
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Unknown error');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [password],
  );

  useEffect(() => {
    fetchOnce();
  }, [fetchOnce]);

  return {
    status,
    data,
    error,
    saving,
    saveError,
    save,
    refetch: fetchOnce,
  };
}
